import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, LoadingController, ToastController } from '@ionic/angular';
import { min } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BaseClass } from 'src/app/shared/classes/base';
import { flatternArray } from 'src/app/shared/helper/arrayHelper';
import { ProcessConfig } from 'src/app/shared/interfaces/process-config.interface';
import { TranslationService } from 'src/app/shared/services/translation.service';

import { Evaluation360Service } from '../../360-evaluation.service';
import { Evaluated360User } from '../../_interfaces/360-evaluation-user.interface';
import { AreaConfig, AreaSelectionError, AreaSelectionErrorEnum } from '../../_interfaces/area-config.interface';
import { ConfigService } from 'src/app/shared/services/multi-clients/config.service';

type DisplayedUser = Evaluated360User & { isReopeningExpired: boolean, isReopenedByRefusal: boolean };
@Component({
    selector: 'app-360-evaluation-user-evaluators',
    templateUrl: './user-evaluators.component.html',
    styleUrls: ['./user-evaluators.component.scss']
})
export class Evaluation360UserEvaluatorsComponent extends BaseClass implements OnDestroy, OnChanges {
    public showThreeSixtyEvaluationLimit: boolean;
    public showAreaEvaluatorsCount: boolean;
    public hideAttributesFromUserSearchOn360: boolean;

    @Input() set userSelected(value: Evaluated360User) {
        this.user = {
            ...value,
            isReopeningExpired: value.reopeningDueDate ? (new Date(value.reopeningDueDate).getTime() < Date.now()) : false,
            isReopenedByRefusal: value.reopeningDueDate ? (new Date(value.reopeningDueDate).getTime() > 0) : false
        };
        this.isReopenedByRefusal = this.user.isReopenedByRefusal;
        this.searchUsers = [];
        this._searchSubject.next('');
    };

    @Input() set processConfig(value: ProcessConfig) {
        if (this.showThreeSixtyEvaluationLimit && value) {
            this.enableValuationLimit = value.enableValuationLimit;
            this.threeSixtyEvaluationLimit = value.threeSixtyEvaluationLimit;
            this.threeSixtyEvaluationTimespanInHours = value.threeSixtyEvaluationTimespanInHours;
        }
    }
    @Input() areas: Array<AreaConfig> = [];
    @Output() wasApproved = new EventEmitter();

    public hideAmountTimeUserWasSelectedOn360;
    public dialogAlertToApproveEvaluatorsEnabled;
    public showSearchWithoutAddClick;
    public mandatoryThreeSixtyAreaRule;

    public user: DisplayedUser;
    public isReopenedByRefusal: boolean = false;
    public searchUsers: Array<Array<Evaluated360User>> = [];
    public enableValuationLimit: boolean = false;
    public threeSixtyEvaluationLimit: number = 10;
    public threeSixtyEvaluationTimespanInHours: number = 99;
    public maxByArea: { [key: number]: number } = {};

    private _cycleId: number;
    private _searchingAreaIndex: number;
    private _searchSubject: Subject<string> = new Subject();

    constructor(
        protected _toastController: ToastController,
        protected _loadingController: LoadingController,
        protected _translationService: TranslationService,
        private _activatedRoute: ActivatedRoute,
        private _evaluationService: Evaluation360Service,
        private _alertController: AlertController,
        private _configService: ConfigService
    ) {
        super(_toastController, _translationService);

        this.showThreeSixtyEvaluationLimit = this._configService.settings.features.showThreeSixtyEvaluationLimit;
        this.showAreaEvaluatorsCount = this._configService.settings.features.showAreaEvaluatorsCount;
        this.hideAttributesFromUserSearchOn360 = this._configService.settings.features.hideAttributesFromUserSearchOn360;
        this.hideAmountTimeUserWasSelectedOn360 = this._configService.settings.features.hideAmountTimeUserWasSelectedOn360;
        this.dialogAlertToApproveEvaluatorsEnabled = this._configService.settings.features.dialogAlertToApproveEvaluatorsEnabled;
        this.showSearchWithoutAddClick = this._configService.settings.features.showSearchWithoutAddClick;
        this.mandatoryThreeSixtyAreaRule = this._configService.settings.features.mandatoryThreeSixtyAreaRule;


        this._cycleId = parseInt(
            this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._setSearchSubscriptions();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.showAreaEvaluatorsCount) return;
        if ((changes.areas && changes.areas.currentValue) || (changes.userSelected && changes.userSelected.currentValue)) {
            this._setMaxByArea();
        }
    }

    public updateSearch(searchTextValue: string, areaIndex: number): void {
        this._searchingAreaIndex = areaIndex;
        this._searchSubject.next(searchTextValue);
    }

    public async selectUser(user: Evaluated360User, areaIndex: number) {
        console.log('selectUser', user, areaIndex);

        if (user.aboveSelectionLimit && this.isReopenedByRefusal) {
            await this.notify(
                `'${user.name}'` + ' ' +
                this._translationService.getTranslation('360evaluation.evaluatorsLimitReached')
                , 'warning', 5000);
            return;
        }
        const userFlags = user.flags;
        const errorMessages: string[] = [];

        const errorTranslations = {
            'Cycles.UserNotInCycle': 'Cycles.UserNotInCycle',
            'generic.InaccessibleUserByChineseWall': 'generic.InaccessibleUserByChineseWall',
            '360evaluation.unavailableManager': '360evaluation.unavailableManager',
            '360evaluation.doesNotHaveAttribute': '360evaluation.doesNotHaveAttribute',
            '360evaluation.360Finished': '360evaluation.evaluatorHas360StepClosed',
            '360evaluation.subordinate': '360evaluation.subordinate',
            'generic.directSubordinates': 'generic.directSubordinates',
            '360evaluation.head': '360evaluation.head'
        };

        for (const flag of userFlags) {
            if (errorTranslations[flag]) {
                let errorMessage = this._translationService.getTranslation(errorTranslations[flag]);

                if (flag === '360evaluation.360Finished') {
                    errorMessage = errorMessage.replace('USER ', user.name);
                }

                errorMessages.push(errorMessage);
            }
        }

        if (errorMessages.length > 0) {
            const combinedErrorMessage = errorMessages.join('\n');
            await this.notify(combinedErrorMessage, 'danger');
            return;
        }

        const evaluators = this.user.areaEvaluators[areaIndex].evaluators;

        if (
            !evaluators.some(u => u.userId === user.userId)
        ) {
            user.areaId = this.user.areaEvaluators[areaIndex].areaId;

            user.selectedCount = user.selectedCount ? user.selectedCount + 1 : 1;
            evaluators.push(user);
        }

        this.user.areaEvaluators[areaIndex].showSearch = false;
        this.searchUsers[areaIndex] = [];
        this._searchSubject.next('');
    }

    public async confirmRemoveEvaluator(
        areaIndex: number, user: Evaluated360User, userIndex: number
    ): Promise<void> {
        let hasEvals = false;

        const alert = await this._alertController.create({
            message: this._translationService.getTranslation('360evaluation.confirmDeleteEvaluator'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    if (hasEvals) {
                        this._confirmEvalsToDelete(areaIndex, user, userIndex);
                    } else {
                        user.areaEvaluators[areaIndex].evaluators.splice(userIndex, 1);
                    }
                }
            }]
        });

        this._evaluationService.getEvalsToDelete(
            user.cycleParticipantId,
            user.areaEvaluators[areaIndex].evaluators[userIndex].cycleParticipantId
        ).subscribe(async () => {
            await alert.present();
        }, async (error) => {
            hasEvals = true;
            await alert.present();
        });
    }

    private _loadUsers(areaIndex: number, searchString: string, userId?: string): void {
        const area = this.user.areaEvaluators[areaIndex];
        let areaId = this.areas.some(area => area.userAttributesType && area.userAttributesType.length > 0) ? area.areaId : 0;
        this._evaluationService.getUsersFor360Evaluation(
            this._cycleId, searchString, userId, area.attributeType, area.attributeAction, areaId
        ).subscribe((response) => {
            this.searchUsers[areaIndex] = response.data;

            if (this.enableValuationLimit) {
                for (const user of this.searchUsers[areaIndex]) {
                    user.aboveSelectionLimit = user.selectedCount >= this.threeSixtyEvaluationLimit;
                }
            }
        }, (error) => this.notifyError(error));
    }

    private async _confirmEvalsToDelete(
        areaIndex: number, user: Evaluated360User, userIndex: number
    ): Promise<void> {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation('360evaluation.evalsToDelete'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    user.areaEvaluators[areaIndex].evaluators.splice(userIndex, 1);
                }
            }]
        });
        await alert.present();
    }

    public async confirmApproveEvaluators(user: Evaluated360User) {
        if (this.dialogAlertToApproveEvaluatorsEnabled) {
            const alert = await this._alertController.create({
                message: this._translationService.getTranslation('360evaluation.confirmApproveEvaluators'),
                buttons: [{
                    text: this._translationService.getTranslation('generic.no'),
                    role: 'cancel',
                    cssClass: 'danger'
                }, {
                    text: this._translationService.getTranslation('generic.yes'),
                    handler: () => {
                        this._approveEvaluators(user);
                    }
                }]
            });
            await alert.present();
        } else
            await this._approveEvaluators(user);
    }

    private async _approveEvaluators(user: Evaluated360User): Promise<void> {

        if (!(this.showThreeSixtyEvaluationLimit && this.enableValuationLimit)) {
            this._sendApproveEvaluators(user);
            return;//feature disabled
        }

        var userEvaluators = flatternArray<Evaluated360User>(user.areaEvaluators.map(area => area.evaluators));
        if (!userEvaluators.some(evaluator => evaluator.selectedCount > this.threeSixtyEvaluationLimit)) {
            this._sendApproveEvaluators(user);
            return;//no evaluator above limit
        }

        const fallbackMsg = `atenção, você está aprovando um ou mais avaliadores que já possui +{{evaluationLimit}} indicações. É possível que a avaliação do seu liderado seja recusada, gostaria de continuar?`
        const msgCode = '360evaluation.validation.aboveLimitMessage';
        const message = this._translationService.getTranslation(msgCode) == msgCode ? fallbackMsg : this._translationService.getTranslation(msgCode);
        const alert = await this._alertController.create({
            message: message.replace('{{evaluationLimit}}', this.threeSixtyEvaluationLimit.toString()),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    this._sendApproveEvaluators(user);
                }
            }]
        });
        await alert.present();
    }

    private async _sendApproveEvaluators(user: Evaluated360User): Promise<void> {
        let evaluators = [];
        user.areaEvaluators.forEach(area => {
            evaluators = [...evaluators, ...area.evaluators];
        });

        let validatingEvaluators: Array<Array<Evaluated360User>> = new Array<Array<Evaluated360User>>();
        evaluators.forEach((evalu) => {
            const areaId = evalu.areaId;
            if (validatingEvaluators && !validatingEvaluators[areaId]) {
                validatingEvaluators[areaId] = [];
            }
            validatingEvaluators[areaId].push(evalu);
        });
        validatingEvaluators = validatingEvaluators.filter((evaluatorArray) => evaluatorArray);

        const error = this._validateSelection(validatingEvaluators, this.areas);
        if (error) {
            await this.notify(this._translationService.getTranslation(error.message) + ': ' + error.value, 'danger', 5000);
        } else {
            this._evaluationService.manageSelectedUsersFor360Evaluation(
                user.cycleParticipantId, evaluators
            ).subscribe(() => {
                this.notifySuccess();
                this.wasApproved.emit();

            }, (error) => this.notifyError(error));
        }
    }

    private _setSearchSubscriptions() {
        this._searchSubject.pipe(
            debounceTime(500)
        ).subscribe((searchValue: string) => {
            this.searchUsers[this._searchingAreaIndex] = [];
            if (searchValue) {
                this._loadUsers(
                    this._searchingAreaIndex,
                    searchValue,
                    this.user.userId
                );
            }
        });
    }

    private _validateSelection(
        selectedUsers: Array<Array<Evaluated360User>>, areas: Array<AreaConfig>
    ): { area: string | null; message: string; value: number; areaLenghtError: boolean } {
        if (this.mandatoryThreeSixtyAreaRule) {
            let error = this._validateMandatoryThreeSixtyAreaLimits(selectedUsers, areas);
            if (error) {
                this.notify(error, 'danger');
                throw new Error('360evaluation.mandatoryAreaLimitNotReached');
            }
        }
        let totalUsers = 0;
        let minEvaluators = 0;
        let maxEvaluators = 0;
        for (const element of areas) {
            minEvaluators += element.minEvaluators;
            maxEvaluators += element.maxEvaluators;
        }
        for (const element of selectedUsers) {
            const users = element;

            if (users.length === 0) {
                continue;
            }

            totalUsers += users.length;
            const areaIndex = areas.findIndex(a =>
                a.areaId === element[0].areaId
            );

            if (!areas[areaIndex].requiresEvaluatorsValidation)
                continue;

            if (users.length < areas[areaIndex].minEvaluators)
                return {
                    areaLenghtError: true,
                    message: '360evaluation.minEvaluatorsRequired',
                    area: areas[areaIndex].globalizationConfig[this.getSelectedLanguage()].title,
                    value: areas[areaIndex].minEvaluators
                };

            if (users.length > areas[areaIndex].maxEvaluators)
                return {
                    areaLenghtError: true,
                    message: '360evaluation.evaluatorsLimitReached',
                    area: areas[areaIndex].globalizationConfig[this.getSelectedLanguage()].title,
                    value: areas[areaIndex].maxEvaluators
                };
        }

        if (this.mandatoryThreeSixtyAreaRule) {
            let error = this._validateMandatoryThreeSixtyAreaLimits(selectedUsers, areas);
            if (error) {
                this.notify(this._translationService.getTranslation('360evaluation.mandatoryAreaLimitNotReached'), 'danger');
                throw new Error('360evaluation.mandatoryAreaLimitNotReached');
            }
        }

        if (totalUsers < minEvaluators)
            return {
                areaLenghtError: false,
                message: '360evaluation.minEvaluatorsRequired',
                area: null,
                value: minEvaluators
            };

        if (totalUsers > maxEvaluators)
            return {
                areaLenghtError: false,
                message: '360evaluation.evaluatorsLimitReached',
                area: null,
                value: maxEvaluators
            };

        return null;
    }

    private _validateMandatoryThreeSixtyAreaLimits(selectedEvaluators: Array<Array<Evaluated360User>>, areas: Array<AreaConfig>) {
        let evaluatorsNumbers: { [key: number]: number } = {};
        let errorsByArea: Array<AreaSelectionError> = [];
        selectedEvaluators.forEach(areaEvaluators => {
            let areaId = areaEvaluators[0].areaId;
            evaluatorsNumbers[areaId] = areaEvaluators.length;
        });
        let areaIds = areas.map(area => area.areaId);
        areaIds.forEach(areaId => {
            if (!evaluatorsNumbers[areaId])
                evaluatorsNumbers[areaId] = 0;
            if (evaluatorsNumbers[areaId] == this.maxByArea[areaId])
                errorsByArea.push({ error: AreaSelectionErrorEnum.NoErrors, areaId: areaId });
            else if (evaluatorsNumbers[areaId] == 0)
                errorsByArea.push({ error: AreaSelectionErrorEnum.ZeroEvaluators, areaId: areaId });
            else if (evaluatorsNumbers[areaId] != this.maxByArea[areaId])
                errorsByArea.push({ error: AreaSelectionErrorEnum.DifferentEvaluators, areaId: areaId });
        })

        if (errorsByArea.every(error => error.error === AreaSelectionErrorEnum.NoErrors))
            return;

        if (errorsByArea.every(error => error.error === AreaSelectionErrorEnum.ZeroEvaluators))
            return this._translationService.getTranslation('360evaluation.zeroEvaluators');

        if (errorsByArea.every(error => error.error === AreaSelectionErrorEnum.DifferentEvaluators))
            return this._translationService.getTranslation('360evaluation.differentEvaluators');

        if (errorsByArea.some(error => error.error === AreaSelectionErrorEnum.ZeroEvaluators)) {
            const areaId = errorsByArea.find(error => error.error === AreaSelectionErrorEnum.ZeroEvaluators).areaId;
            let area = areas.find(area => area.areaId === areaId);
            return this._translationService.getTranslation('360evaluation.zeroEvaluatorInArea') + " " + area.globalizationConfig[this.getSelectedLanguage()].title;
        }

        if (errorsByArea.some(error => error.error === AreaSelectionErrorEnum.DifferentEvaluators)) {
            const areaId = errorsByArea.find(error => error.error === AreaSelectionErrorEnum.DifferentEvaluators).areaId;
            let area = areas.find(area => area.areaId === areaId);
            return this._translationService.getTranslation('360evaluation.differentEvaluatorsInArea') + " " + area.globalizationConfig[this.getSelectedLanguage()].title;
        }

    }

    private _setMaxByArea() {
        if (!this.areas || this.areas.length === 0) {
            return;
        }
        this.areas.forEach(area => {
            let userArea = this.user.areaEvaluators.find(ae => ae.areaId === area.areaId);
            this.maxByArea[area.areaId] = min([area.maxEvaluators, userArea.usersOfArea]);
        });
        return;
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }
}
